import type { ExtractPreviewResponse, StandardExtractSection } from '@/shared/hooks/api/userStandards'
import { getExtractionPreviewUiState } from '../components/extraction-preview/standardExtractionPreviewUiState'
import { isExtractPreviewSectionNotFound } from './extractionPreviewUi'
import type { ExtractDialogStep } from '../hooks/useStandardSectionExtraction'
import type { TFunction } from 'i18next'

export type PostExtractDialogOutcome =
  | { kind: 'failed'; errorMessage: string }
  | { kind: 'notFound' }
  | { kind: 'found' }

export function resolvePostExtractDialogOutcome(
  section: StandardExtractSection,
  preview: ExtractPreviewResponse,
  translate: TFunction<'standardsLibrary'>
): PostExtractDialogOutcome {
  const uiState = getExtractionPreviewUiState(section, preview, translate)

  if (uiState.allFailed) {
    if (uiState.pdfImpediment != null) {
      const code = uiState.pdfImpediment.reason
      return {
        kind: 'failed',
        errorMessage: translate(
          `detail.extractPreview.impedimentReason.${code}`,
          code
        ),
      }
    }
    if (uiState.sectionImpediment != null) {
      return {
        kind: 'failed',
        errorMessage: translate(
          `detail.extractPreview.impedimentReason.${uiState.sectionImpediment.reason}`,
          uiState.sectionImpediment.reason
        ),
      }
    }
    return {
      kind: 'failed',
      errorMessage: translate('detail.extractPreview.allFailed'),
    }
  }

  if (isExtractPreviewSectionNotFound(section, preview)) {
    return { kind: 'notFound' }
  }

  return { kind: 'found' }
}

export function postExtractOutcomeToDialogStep(
  outcome: PostExtractDialogOutcome
): ExtractDialogStep {
  if (outcome.kind === 'failed') {
    return 'extract'
  }
  if (outcome.kind === 'notFound') {
    return 'acknowledgeNotFound'
  }
  return 'review'
}

export const EXTRACT_DIALOG_STEPS_SCOPE: ExtractDialogStep[] = [
  'extract',
  'acknowledgeNotFound',
  'review',
  'save',
]

export const EXTRACT_DIALOG_STEPS_TRANSLATABLE: ExtractDialogStep[] = [
  'extract',
  'acknowledgeNotFound',
  'review',
  'translate',
  'reviewTranslations',
  'save',
]

export const EXTRACT_DIALOG_TRANSLATION_STEPS: ExtractDialogStep[] = [
  'translate',
  'reviewTranslations',
]

export function resolveSkippedDialogSteps(
  sectionNotFoundFlow: boolean,
  supportsTranslation: boolean
): ExtractDialogStep[] {
  if (!sectionNotFoundFlow) {
    return []
  }
  if (supportsTranslation) {
    return ['review', ...EXTRACT_DIALOG_TRANSLATION_STEPS, 'save']
  }
  return ['review', 'save']
}
